import React, { useState, useEffect } from 'react';
import { Calculator, Mail, UserMinus, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const EmailUnsubscribeRateCalculator = () => {
  const [emailsSent, setEmailsSent] = useState(10000);
  const [unsubscribes, setUnsubscribes] = useState(50);
  const [unsubscribeRate, setUnsubscribeRate] = useState(0);

  useEffect(() => {
    calculateUnsubscribeRate();
  }, [emailsSent, unsubscribes]);

  const calculateUnsubscribeRate = () => {
    const rate = (unsubscribes / emailsSent) * 100;
    setUnsubscribeRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/email-unsubscribe-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Email Unsubscribe Rate Calculator: Reduce List Churn | Free Tool</title>
        <meta name="description" content="Calculate your Email Unsubscribe Rate instantly. Compare with industry benchmarks, get retention strategies, and improve your email list health." />
        <meta name="keywords" content="email unsubscribe rate calculator, list churn rate, email list retention, subscriber engagement, email marketing metrics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Unsubscribe Rate Calculator: Optimize List Retention" />
        <meta property="og:description" content="Calculate your Email Unsubscribe Rate instantly. Get actionable insights to reduce list churn and improve subscriber retention." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Unsubscribe Rate Calculator: Optimize List Retention" />
        <meta property="twitter:description" content="Calculate your Email Unsubscribe Rate instantly. Get actionable insights to reduce list churn and improve subscriber retention." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Email Unsubscribe Rate Calculator",
            "description": "Free calculator to measure and optimize email unsubscribe rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Unsubscribe Rate",
              "Industry Benchmarks",
              "Retention Strategies",
              "List Health Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Email Unsubscribe Rate",
              "description": "The percentage of email subscribers who opt out of receiving future emails from your list."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Email Marketers, Digital Marketers, Newsletter Publishers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Email Unsubscribe Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of emails sent"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of unsubscribes"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your unsubscribe rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Email Unsubscribe Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Emails Sent</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={emailsSent}
                  onChange={(e) => setEmailsSent(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Unsubscribes</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserMinus className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={unsubscribes}
                  onChange={(e) => setUnsubscribes(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {unsubscribeRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Email Unsubscribe Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Email Unsubscribe Rate is {unsubscribeRate}%. This means that {unsubscribeRate}% of recipients opted out of your email list after receiving an email.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 0.20-0.40%</li>
          <li>B2B Services: 0.15-0.30%</li>
          <li>Retail: 0.25-0.40%</li>
          <li>Technology: 0.20-0.35%</li>
          <li>Media/Publishing: 0.30-0.50%</li>
          <li>Nonprofits: 0.15-0.25%</li>
          <li>Education: 0.15-0.30%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Retention Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Segment your email list</li>
          <li>Provide valuable content</li>
          <li>Optimize send frequency</li>
          <li>Use double opt-in</li>
          <li>Offer preference centers</li>
          <li>Personalize content</li>
          <li>Set clear expectations</li>
          <li>Clean your list regularly</li>
          <li>Monitor engagement metrics</li>
          <li>Test different content types</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Common Unsubscribe Triggers</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Too frequent emails (45%)</li>
          <li>Irrelevant content (37%)</li>
          <li>Too promotional (32%)</li>
          <li>Poor mobile experience (24%)</li>
          <li>Changed interests (21%)</li>
          <li>Poor targeting (18%)</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Each unsubscribe costs 5-10x the acquisition cost</li>
          <li>Segmentation reduces unsubscribes by 28%</li>
          <li>Preference centers decrease rates by 25%</li>
          <li>Personalization lowers unsubscribes by 17%</li>
          <li>Optimal frequency reduces rates by 40%</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailUnsubscribeRateCalculator;
